import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// --- external imports ------

import { useUserContext } from '~/authentication/UserProvider';
import classifyAuthError, {
	AUTH_ERROR_CLASS,
	isRetryableAuthError
} from '~/authentication/lib/classifyAuthError';
// --- internal imports ------

/**
 * Owns an auth screen's side of `AuthErrorScreen`.
 *
 * Every screen that hands its failures to the error screen has the same three
 * jobs — read the provider's failure, decide whether it is one the error screen
 * takes, and get out of the way once a retry succeeds — and the two that did it
 * by hand had already drifted apart on the third. Keeping it here is what stops
 * a fix landing on one screen and not the other.
 *
 * `onSettled` releases whatever loading state the screen was holding for the
 * request. `onCredentialsError` is offered a failure that belongs on the
 * screen's own fields; a screen that has no field for one simply omits it.
 */
function useAuthErrorScreen(options) {
	const [screenError, setScreenError] = useState(null);

	const navigate = useNavigate();
	const {
		authError,
		setAuthError,
		isAuthBusy,
		retryAuthCheck,
		tradersUserData,
		logOutPlayer
	} = useUserContext();

	const onSettled = options?.onSettled;
	const onCredentialsError = options?.onCredentialsError;

	// Errors raised by `UserProvider` — the user system refusing the player after
	// Firebase already accepted them. What the player is shown follows what the
	// failure means, not whether the form happens to be filled in: keying it on an
	// empty form used to drop a lapsed licence on a cold load, which is exactly the
	// case that most needs explaining.
	useEffect(function readAuthErrorFromProvider() {
		if (!authError) {
			return;
		}

		const errorClass = classifyAuthError(authError);

		setAuthError(null);
		onSettled?.();

		if (errorClass === AUTH_ERROR_CLASS.TERMINAL || errorClass === AUTH_ERROR_CLASS.TRANSIENT) {
			setScreenError(authError);
			return;
		}

		// Neither remaining class has a screen of its own: a session that merely
		// ended is answered by the login form, and a refused credential belongs on
		// that form's own fields. Either way, a screen still up from an earlier
		// failure now describes something that is no longer what went wrong — and
		// worse, its Try Again would run against a session that has since been
		// cleared, which makes no request at all. So it comes down here rather than
		// wedging the player behind stale copy and a dead button.
		setScreenError(null);

		if (errorClass === AUTH_ERROR_CLASS.CREDENTIALS) {
			onCredentialsError?.(authError);
		}
	}, [authError, setAuthError, onSettled, onCredentialsError]);

	useEffect(function leaveScreenOnSuccess() {
		if (!screenError || !tradersUserData) {
			return;
		}

		setScreenError(null);

		// A retry that reached the user system leaves the route to redirect on its
		// own — but only when Firebase is on the context too, and it is not always.
		// A session restored from a stored token under NONE persistence never sets
		// `firebaseUser`, so `AuthRoute` sees half a session, redirects on neither
		// half, and strands the player on an empty login form holding a session
		// that works. Naming the destination costs nothing when the route would
		// have gone there anyway.
		navigate('/game/', { replace: true });
	}, [screenError, tradersUserData, navigate]);

	const clearScreenError = useCallback(() => {
		// A transient failure kept the Firebase session deliberately, so that the
		// retry could reuse it. Dismissing the screen gives that retry up, and a
		// standing session is then worse than none: Firebase notifies its listeners
		// only when the uid changes, so re-submitting the account that is already
		// signed in resolves without `UserProvider` ever hearing about it — the
		// login never runs and the form holds its cursor for ever.
		if (isRetryableAuthError(screenError)) {
			logOutPlayer();
		}

		setScreenError(null);
	}, [screenError, logOutPlayer]);

	return {
		screenError,
		clearScreenError,
		onRetry: retryAuthCheck,
		isRetrying: isAuthBusy
	};
}

export default useAuthErrorScreen;
